import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, VStack, Text, SimpleGrid } from '@chakra-ui/react';
import {
  Channel,
  ChannelDefinition,
  mediaChannelDefinitions,
  kpiChannelDefinitions,
  SERVICES_REQUIRING_ACCOUNT_SELECTION,
} from 'src/features/Settings/utils/constants';
import ConnectionItem from 'src/features/Settings/components/molecules/ConnectionItem';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import {
  IntegrationService,
  useDataConnectorsQuery,
  zIntegrationService,
} from 'src/api/queries';
import {
  useDisconnectDataConnectorMutation,
  useUpdateSelectedAccountsMutation,
  useUpdateCampaignFilterMutation,
} from 'src/api/mutations';
import { useAppContext } from 'src/contexts/AppContext';
import { formatRelativeTimeFromNow } from 'src/utils/date';
import { API_BASE_URL } from 'src/constants';
import { z } from 'zod';
import { Button } from 'src/components/atoms/Button';
import { DisconnectDialog } from 'src/features/Settings/components/molecules/DisconnectDialog';
import { SelectAccountsModal } from 'src/features/Settings/components/molecules/SelectAccountsModal';
import { CampaignFilterModal } from 'src/features/Settings/components/molecules/CampaignFilterModal';

export const ConnectionsTab: React.FC = () => {
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();
  const { currentCustomer } = useAppContext();
  const customerName = currentCustomer?.customerName ?? '';
  const allDefinitions = useMemo(
    () => [...mediaChannelDefinitions, ...kpiChannelDefinitions],
    [],
  );

  const {
    data: connectorsData,
    isLoading: isLoadingConnectors,
    isFetching: isFetchingConnectors,
    refetch: refetchConnectors,
  } = useDataConnectorsQuery({ customer: customerName }, Boolean(customerName));

  const connectorsByService = useMemo(() => {
    return new Map(
      (connectorsData ?? []).map((connector) => [connector.service, connector]),
    );
  }, [connectorsData]);

  const buildChannels = useCallback(
    (definitions: ChannelDefinition[]): Channel[] => {
      return definitions.map((definition) => {
        const connector = connectorsByService.get(definition.service);

        if (!connector) {
          return {
            ...definition,
            accountName: '',
            status: 'not-connected',
            selectedAccounts: null,
            actions: [],
          };
        }

        if (connector.status === 'success') {
          const connectedAccountName =
            definition.service === 'shopify'
              ? (connector.selectedAccounts?.[0] ??
                definition.defaultAccountName ??
                `${definition.name} account`)
              : (definition.defaultAccountName ?? `${definition.name} account`);

          return {
            ...definition,
            accountName: connectedAccountName,
            status: 'connected' as const,
            lastConnected: formatRelativeTimeFromNow(connector.updatedAt),
            selectedAccounts: connector.selectedAccounts,
            campaignNameFilter: connector.campaignNameFilter ?? null,
            actions: ['edit'],
          };
        }

        return {
          ...definition,
          accountName:
            definition.defaultAccountName ?? `${definition.name} account`,
          status: 'failed' as const,
          selectedAccounts: connector.selectedAccounts,
          actions: ['reconnect'],
        };
      });
    },
    [connectorsByService],
  );

  const mediaChannels = useMemo(
    () => buildChannels(mediaChannelDefinitions),
    [buildChannels],
  );

  const kpiChannels = useMemo(
    () => buildChannels(kpiChannelDefinitions),
    [buildChannels],
  );

  const handleConnectionSuccess = useCallback(
    (channelName: string) => {
      showSuccess(
        `${channelName} connected successfully`,
        'Data will now sync automatically',
      );
    },
    [showSuccess],
  );

  const handleConnectionFailure = useCallback(
    (channelName: string) => {
      showError(
        `${channelName} connection failed`,
        'Connection failed. Please try again.',
      );
    },
    [showError],
  );

  const { mutate: disconnectConnector, isLoading: isDisconnecting } =
    useDisconnectDataConnectorMutation();

  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [channelToDisconnect, setChannelToDisconnect] =
    useState<Channel | null>(null);

  const handleDisconnect = useCallback((channel: Channel) => {
    setChannelToDisconnect(channel);
    setIsDisconnectDialogOpen(true);
  }, []);

  const handleConfirmDisconnect = useCallback(() => {
    if (!customerName || !channelToDisconnect) return;

    disconnectConnector(
      {
        customer: customerName,
        service: channelToDisconnect.service as IntegrationService,
      },
      {
        onSuccess: () => {
          showSuccess(
            `${channelToDisconnect.name} disconnected`,
            'The connection has been removed.',
          );
          setIsDisconnectDialogOpen(false);
          setChannelToDisconnect(null);
          void refetchConnectors();
        },
        onError: () => {
          showError(
            `Failed to disconnect ${channelToDisconnect.name}`,
            'Please try again or contact support.',
          );
        },
      },
    );
  }, [
    customerName,
    channelToDisconnect,
    disconnectConnector,
    refetchConnectors,
    showSuccess,
    showError,
  ]);

  const { mutate: updateSelectedAccounts, isLoading: isSavingAccounts } =
    useUpdateSelectedAccountsMutation();

  const [isSelectAccountsOpen, setIsSelectAccountsOpen] = useState(false);
  const [channelForAccountSelection, setChannelForAccountSelection] =
    useState<Channel | null>(null);

  const handleSelectAccounts = useCallback((channel: Channel) => {
    setChannelForAccountSelection(channel);
    setIsSelectAccountsOpen(true);
  }, []);

  const handleSaveSelectedAccounts = useCallback(
    (selectedAccountIds: string[]) => {
      if (!customerName || !channelForAccountSelection) return;

      updateSelectedAccounts(
        {
          customer: customerName,
          service: channelForAccountSelection.service as IntegrationService,
          selectedAccountIds,
        },
        {
          onSuccess: () => {
            showSuccess(
              `${channelForAccountSelection.name} accounts updated`,
              'Data will now sync for selected accounts.',
            );
            setIsSelectAccountsOpen(false);
            setChannelForAccountSelection(null);
            void refetchConnectors();
          },
          onError: (error) => {
            const defaultMessage = 'Please try again or contact support.';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError(
              `Failed to update ${channelForAccountSelection.name} accounts`,
              errorMessage,
            );
          },
        },
      );
    },
    [
      customerName,
      channelForAccountSelection,
      updateSelectedAccounts,
      refetchConnectors,
      showSuccess,
      showError,
    ],
  );

  const { mutate: updateCampaignFilter, isLoading: isSavingCampaignFilter } =
    useUpdateCampaignFilterMutation();

  const [isCampaignFilterOpen, setIsCampaignFilterOpen] = useState(false);
  const [channelForCampaignFilter, setChannelForCampaignFilter] =
    useState<Channel | null>(null);

  const handleCampaignFilter = useCallback((channel: Channel) => {
    setChannelForCampaignFilter(channel);
    setIsCampaignFilterOpen(true);
  }, []);

  const handleSaveCampaignFilter = useCallback(
    (campaignNameFilter: string | null) => {
      if (!customerName || !channelForCampaignFilter) return;

      updateCampaignFilter(
        {
          customer: customerName,
          service: channelForCampaignFilter.service as IntegrationService,
          campaignNameFilter,
        },
        {
          onSuccess: () => {
            showSuccess(
              `${channelForCampaignFilter.name} campaign filter updated`,
              'Matching campaigns are being re-imported.',
            );
            setIsCampaignFilterOpen(false);
            setChannelForCampaignFilter(null);
            void refetchConnectors();
          },
          onError: (error) => {
            const defaultMessage = 'Please try again or contact support.';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError(
              `Failed to update ${channelForCampaignFilter.name} campaign filter`,
              errorMessage,
            );
          },
        },
      );
    },
    [
      customerName,
      channelForCampaignFilter,
      updateCampaignFilter,
      refetchConnectors,
      showSuccess,
      showError,
    ],
  );

  const connectedServices = useMemo(() => {
    return (connectorsData ?? [])
      .filter((connector) => connector.status === 'success')
      .map((connector) => connector.service);
  }, [connectorsData]);

  const previousConnectedServices = useRef<string[]>([]);

  useEffect(() => {
    if (!customerName) {
      return;
    }

    if (previousConnectedServices.current.length === 0) {
      previousConnectedServices.current = connectedServices;
      return;
    }

    const newlyConnected = connectedServices.filter(
      (service) => !previousConnectedServices.current.includes(service),
    );

    newlyConnected.forEach((service) => {
      const definition = allDefinitions.find(
        (item) => item.service === service,
      );
      handleConnectionSuccess(definition?.name ?? service);
    });

    previousConnectedServices.current = connectedServices;
  }, [
    allDefinitions,
    connectedServices,
    customerName,
    handleConnectionSuccess,
  ]);

  const zDataConnectionMessage = useMemo(
    () =>
      z.object({
        ok: z.boolean(),
        integration: zIntegrationService.optional(),
        customer: z.string().optional(),
        messageType: z.literal('oauth-callback'),
      }),
    [],
  );

  useEffect(() => {
    const handleOauthMessage = (event: MessageEvent) => {
      if (event.origin !== API_BASE_URL) {
        return;
      }

      const { data, error } = zDataConnectionMessage.safeParse(event.data);
      if (error) {
        return;
      }

      if (!data.ok) {
        const definition = allDefinitions.find(
          (item) => item.service === data.integration,
        );
        const displayName =
          definition?.name ?? data.integration ?? 'Unknown service';

        handleConnectionFailure(displayName);
      }

      if (!data.customer || data.customer !== customerName) {
        return;
      }

      void refetchConnectors();

      if (
        data.ok &&
        data.integration &&
        SERVICES_REQUIRING_ACCOUNT_SELECTION.includes(data.integration)
      ) {
        const definition = allDefinitions.find(
          (item) => item.service === data.integration,
        );
        if (definition) {
          setChannelForAccountSelection({
            ...definition,
            accountName: '',
            status: 'connected',
            selectedAccounts: null,
            actions: ['edit'],
          });
          setIsSelectAccountsOpen(true);
        }
      }
    };

    window.addEventListener('message', handleOauthMessage);
    return () => {
      window.removeEventListener('message', handleOauthMessage);
    };
  }, [
    allDefinitions,
    customerName,
    handleConnectionFailure,
    refetchConnectors,
    zDataConnectionMessage,
  ]);

  const isLoading = isLoadingConnectors || isFetchingConnectors;

  return (
    <VStack
      spacing={{ base: 6, md: 8 }}
      align="stretch"
      border="1px solid"
      borderColor="border.light.box"
      borderRadius="16px"
      maxWidth={'960px'}
      p={{ base: 4, md: 8 }}>
      {/* Header */}
      <Box>
        <Text fontFamily="marketingPage" fontWeight="bold" color="text.title">
          EDIT CONNECTIONS TO YOUR CHANNELS
        </Text>
      </Box>

      {/* Media Channels Section */}
      <Box>
        <Text
          fontFamily="marketingPage"
          fontWeight="semibold"
          mb={{ base: 4, md: 7 }}
          color="text.title">
          Media Channels
        </Text>
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacingY={{ base: 4, md: 6 }}
          spacingX={{ base: 0, md: 6 }}>
          {mediaChannels.map((channel) => (
            <ConnectionItem
              key={channel.id}
              channel={channel}
              onConnectionFailure={handleConnectionFailure}
              onDisconnect={() => handleDisconnect(channel)}
              onSelectAccounts={() => handleSelectAccounts(channel)}
              onCampaignFilter={() => handleCampaignFilter(channel)}
              isLoading={isLoading || isDisconnecting}
            />
          ))}
        </SimpleGrid>
      </Box>

      {/* KPI Channels Section */}
      <Box>
        <Text
          fontFamily="marketingPage"
          fontWeight="semibold"
          mb={{ base: 4, md: 7 }}
          color="text.title">
          KPI Channels
        </Text>
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacingY={{ base: 4, md: 6 }}
          spacingX={{ base: 0, md: 6 }}>
          {kpiChannels.map((channel) => (
            <ConnectionItem
              key={channel.id}
              channel={channel}
              onConnectionFailure={handleConnectionFailure}
              onDisconnect={() => handleDisconnect(channel)}
              onSelectAccounts={() => handleSelectAccounts(channel)}
              isLoading={isLoading || isDisconnecting}
            />
          ))}
        </SimpleGrid>
      </Box>

      {/* Save Changes Button */}
      <Box textAlign={{ base: 'center', md: 'right' }}>
        <Button
          variant="primary"
          size={{ base: 'md', md: 'lg' }}
          px={{ base: 8, md: 16 }}
          py={{ base: 4, md: 6 }}
          width={{ base: '100%', md: 'auto' }}
          buttonName="save-connections-settings">
          Save Changes
        </Button>
      </Box>

      {/* Custom Toast */}
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />

      <DisconnectDialog
        isOpen={isDisconnectDialogOpen}
        onClose={() => setIsDisconnectDialogOpen(false)}
        onConfirm={handleConfirmDisconnect}
        channelName={channelToDisconnect?.name ?? ''}
        isLoading={isDisconnecting}
      />

      {channelForAccountSelection && (
        <SelectAccountsModal
          isOpen={isSelectAccountsOpen}
          onClose={() => setIsSelectAccountsOpen(false)}
          onSave={handleSaveSelectedAccounts}
          service={channelForAccountSelection.service as IntegrationService}
          customer={customerName}
          isSaving={isSavingAccounts}
          onError={showError}
        />
      )}

      {channelForCampaignFilter && (
        <CampaignFilterModal
          isOpen={isCampaignFilterOpen}
          onClose={() => setIsCampaignFilterOpen(false)}
          onSave={handleSaveCampaignFilter}
          currentFilter={channelForCampaignFilter.campaignNameFilter ?? null}
          isSaving={isSavingCampaignFilter}
          customer={customerName}
        />
      )}
    </VStack>
  );
};
